import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Progress, Row, Space, Tag } from 'antd';
import {
  ApiOutlined,
  AppstoreOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  CommentOutlined,
  ClockCircleOutlined,
  CloudServerOutlined,
  FileTextOutlined,
  ExportOutlined,
  ProjectOutlined,
  RobotOutlined,
  TeamOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { resolveUrl } from '../utils/url';
import {
  applicationsApi,
  documentsApi,
  employeesApi,
  mcpServersApi,
  productsApi,
  skillsApi,
  ticketsApi,
  toolsApi,
} from '../services/api-services';
import './Dashboard.css';

type ApiRecord = Record<string, any>;

type DashboardData = {
  applications: ApiRecord[];
  employees: ApiRecord[];
  tickets: ApiRecord[];
  skills: ApiRecord[];
  tools: ApiRecord[];
  mcpServers: ApiRecord[];
  documents: ApiRecord[];
  products: ApiRecord[];
};

const emptyDashboardData: DashboardData = {
  applications: [],
  employees: [],
  tickets: [],
  skills: [],
  tools: [],
  mcpServers: [],
  documents: [],
  products: [],
};

const dataKeys: (keyof DashboardData)[] = [
  'applications',
  'employees',
  'tickets',
  'skills',
  'tools',
  'mcpServers',
  'documents',
  'products',
];

const dataLabels: Record<keyof DashboardData, string> = {
  applications: '应用',
  employees: '数字员工',
  tickets: '工单',
  skills: '技能',
  tools: '工具',
  mcpServers: 'MCP 服务',
  documents: '知识文档',
  products: '产物',
};

const normalizeList = (value: any): ApiRecord[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const getPercent = (value: number, total: number) => total > 0 ? Math.round((value / total) * 100) : 0;

const AnimatedNumber: React.FC<{ value: number; className?: string }> = ({ value, className }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let frameId = 0;
    const duration = 720;
    const startedAt = performance.now();

    setDisplayValue((currentValue) => {
      const from = currentValue;
      const diff = value - from;

      const tick = (now: number) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplayValue(Math.round(from + diff * eased));
        if (progress < 1) {
          frameId = requestAnimationFrame(tick);
        }
      };

      frameId = requestAnimationFrame(tick);
      return currentValue;
    });

    return () => cancelAnimationFrame(frameId);
  }, [value]);

  return <span className={className}>{displayValue}</span>;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [data, setData] = useState<DashboardData>(emptyDashboardData);
  const [loading, setLoading] = useState(false);
  const [failedSections, setFailedSections] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;

    const fetchDashboardData = async () => {
      setLoading(true);
      const requests = [
        applicationsApi.list(),
        employeesApi.list(),
        ticketsApi.list(),
        skillsApi.list(),
        toolsApi.list(),
        mcpServersApi.list(),
        documentsApi.list(),
        productsApi.list(),
      ];

      const settled = await Promise.allSettled(requests);
      if (!mounted) return;

      const nextData: DashboardData = { ...emptyDashboardData };
      const nextFailedSections: string[] = [];

      settled.forEach((result, index) => {
        const key = dataKeys[index];
        if (result.status === 'fulfilled') {
          nextData[key] = normalizeList(result.value);
        } else {
          nextFailedSections.push(dataLabels[key]);
        }
      });

      setData(nextData);
      setFailedSections(nextFailedSections);
      setLoading(false);
    };

    fetchDashboardData();

    return () => {
      mounted = false;
    };
  }, []);

  const stats = useMemo(() => {
    const completedTickets = data.tickets.filter((ticket) => ticket.status === 'completed').length;
    const activeTickets = data.tickets.filter((ticket) => ['processing', 'manual_review', 'pending_ai_continue'].includes(ticket.status)).length;
    const installedEmployees = data.employees.filter((employee) => employee.install_status === 'installed').length;

    return {
      applications: data.applications.length,
      employees: data.employees.length,
      tickets: data.tickets.length,
      completedTickets,
      activeTickets,
      installedEmployees,
      skills: data.skills.length,
      tools: data.tools.length,
      mcpServers: data.mcpServers.length,
      documents: data.documents.length,
      products: data.products.length,
    };
  }, [data]);

  const ticketStatusItems = useMemo(() => [
    { key: 'processing', label: '处理中', value: data.tickets.filter((ticket) => ticket.status === 'processing').length, color: '#0071e3' },
    { key: 'pending_evaluation', label: '待评价', value: data.tickets.filter((ticket) => ticket.status === 'pending_evaluation').length, color: '#ff9f0a' },
    { key: 'manual_review', label: '人工审核', value: data.tickets.filter((ticket) => ticket.status === 'manual_review').length, color: '#ff3b30' },
    { key: 'completed', label: '已完成', value: stats.completedTickets, color: '#34c759' },
  ], [data.tickets, stats.completedTickets]);

  const employeeStatusItems = useMemo(() => [
    { key: 'installed', label: '已安装', value: data.employees.filter((employee) => employee.install_status === 'installed').length, color: '#34c759' },
    { key: 'installing', label: '安装中', value: data.employees.filter((employee) => employee.install_status === 'installing').length, color: '#0071e3' },
    { key: 'failed', label: '失败', value: data.employees.filter((employee) => employee.install_status === 'failed').length, color: '#ff3b30' },
    { key: 'idle', label: '待安装', value: data.employees.filter((employee) => !employee.install_status || employee.install_status === 'idle').length, color: '#8e8e93' },
  ], [data.employees]);

  const metricCards = [
    { title: '应用', value: stats.applications, description: '面向业务交付的智能体应用', icon: <AppstoreOutlined />, accent: 'blue' },
    { title: '数字员工', value: stats.employees, description: `${stats.installedEmployees} 个已安装可运行`, icon: <TeamOutlined />, accent: 'purple' },
    { title: '工单', value: stats.tickets, description: `${stats.activeTickets} 个仍在处理中`, icon: <FileTextOutlined />, accent: 'orange' },
    { title: '已完成', value: stats.completedTickets, description: '任务闭环与交付成果', icon: <CheckCircleOutlined />, accent: 'green' },
    { title: '技能', value: stats.skills, description: '可复用 Agent 能力', icon: <ToolOutlined />, accent: 'blue' },
    { title: '工具', value: stats.tools, description: '脚本与自动化执行单元', icon: <ApiOutlined />, accent: 'green' },
    { title: 'MCP', value: stats.mcpServers, description: '外部上下文与工具服务', icon: <CloudServerOutlined />, accent: 'purple' },
    { title: '知识文档', value: stats.documents, description: '企业知识与语料资产', icon: <ProjectOutlined />, accent: 'orange' },
  ];

  const quickEmployees = data.employees.slice(0, 6);
  const quickApplications = data.applications.slice(0, 6);

  const openStandaloneApplication = (application: ApiRecord) => {
    if (!application.has_static) {
      navigate(`/app/${application.slug || application.id}/info`, { state: { activeTab: 'static' } });
      return;
    }

    const standaloneId = application.slug || application.id;
    const standaloneUrl = `/app/${standaloneId}/index?theme=${theme}`;
    if ((window as any).__TAURI__) {
      import('@tauri-apps/api/webviewWindow').then(({ WebviewWindow }) => {
        new WebviewWindow(`standalone-${application.id}`, {
          url: `index.html?__route=${encodeURIComponent(standaloneUrl)}`,
          title: application.name || '应用',
          width: 1200,
          height: 800,
        });
      });
    } else {
      window.open(standaloneUrl, '_blank');
    }
  };

  return (
    <div className="apple-page dashboard-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero-content">
          <h1 className="dashboard-hero-title">A+ 智能体编排系统</h1>
          <p className="dashboard-hero-subtitle">
            统一观察应用、数字员工、技能工具与工单执行状态，快速判断平台能力池与任务闭环是否健康。
          </p>
        </div>
        <div className="dashboard-summary-pills">
          <button className="dashboard-summary-pill" onClick={() => navigate('/tickets')}>
            <ClockCircleOutlined />
            <span>运行中工单</span>
            <strong><AnimatedNumber value={stats.activeTickets} /></strong>
            <small>查看</small>
          </button>
          <button className="dashboard-summary-pill" onClick={() => navigate('/employees')}>
            <RobotOutlined />
            <span>已安装员工</span>
            <strong><AnimatedNumber value={stats.installedEmployees} /></strong>
            <small>查看</small>
          </button>
          <button className="dashboard-summary-pill" onClick={() => navigate('/products')}>
            <ProjectOutlined />
            <span>产物输出</span>
            <strong><AnimatedNumber value={stats.products} /></strong>
            <small>查看</small>
          </button>
        </div>
      </section>

      {failedSections.length > 0 && (
        <Alert
          className="dashboard-alert"
          type="warning"
          showIcon
          message={`部分数据暂不可用：${failedSections.join('、')}`}
        />
      )}

      <Row gutter={[16, 16]} className="dashboard-metric-grid">
        {metricCards.map((card) => (
          <Col xs={12} sm={12} md={8} xl={6} key={card.title}>
            <Card className={`apple-stat-card dashboard-stat-card dashboard-accent-${card.accent}`} variant="borderless" loading={loading}>
              <div className="dashboard-stat-topline">
                <span className="dashboard-stat-icon">{card.icon}</span>
                <span className="dashboard-stat-label">{card.title}</span>
              </div>
              <AnimatedNumber className="dashboard-stat-number" value={card.value} />
              <p>{card.description}</p>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[20, 20]} className="dashboard-section">
        <Col xs={24} lg={12}>
          <Card className="dashboard-status-card" variant="borderless" loading={loading}>
            <div className="dashboard-section-title">
              <div>
                <h3>工单执行状态</h3>
                <p>关注任务处理、评价和人工介入队列。</p>
              </div>
              <Tag color="blue">共 {stats.tickets} 条</Tag>
            </div>
            <Space direction="vertical" size={16} className="dashboard-progress-list">
              {ticketStatusItems.map((item) => (
                <div className="dashboard-progress-item" key={item.key}>
                  <div>
                    <span>{item.label}</span>
                    <strong><AnimatedNumber value={item.value} /></strong>
                  </div>
                  <Progress percent={getPercent(item.value, stats.tickets)} showInfo={false} strokeColor={item.color} />
                </div>
              ))}
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className="dashboard-status-card" variant="borderless" loading={loading}>
            <div className="dashboard-section-title">
              <div>
                <h3>数字员工安装状态</h3>
                <p>确认 Agent 是否已具备运行条件。</p>
              </div>
              <Tag color="green">{stats.installedEmployees} 可用</Tag>
            </div>
            <Space direction="vertical" size={16} className="dashboard-progress-list">
              {employeeStatusItems.map((item) => (
                <div className="dashboard-progress-item" key={item.key}>
                  <div>
                    <span>{item.label}</span>
                    <strong><AnimatedNumber value={item.value} /></strong>
                  </div>
                  <Progress percent={getPercent(item.value, stats.employees)} showInfo={false} strokeColor={item.color} />
                </div>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={[20, 20]} className="dashboard-section">
        <Col xs={24} xl={12}>
          <Card className="dashboard-status-card" variant="borderless" loading={loading}>
            <div className="dashboard-section-title">
              <div>
                <h3>数字人快捷入口</h3>
                <p>快速与数字人对话。</p>
              </div>
              <Button type="link" onClick={() => navigate('/employees')}>查看全部</Button>
            </div>
            <div className="dashboard-employee-shortcuts">
              {quickEmployees.map((employee) => (
                <button className="dashboard-employee-shortcut" key={employee.id} onClick={() => navigate(`/employees/${employee.id}/chat`)}>
                  <span className={`dashboard-employee-avatar dashboard-employee-avatar-${employee.employee_type || 'technical'}`}>
                    {employee.avatar ? (
                      <img src={employee.avatar.startsWith('/uploads/') ? resolveUrl(employee.avatar) : employee.avatar} alt={employee.name} />
                    ) : (
                      <RobotOutlined />
                    )}
                  </span>
                  <span className="dashboard-employee-shortcut-info">
                    <strong>{employee.name || '未命名数字人'}</strong>
                    <small>{employee.description || employee.identifier || '暂无描述'}</small>
                  </span>
                  <span className="dashboard-shortcut-action dashboard-shortcut-action-chat"><CommentOutlined />对话</span>
                </button>
              ))}
              {!loading && quickEmployees.length === 0 && (
                <button className="dashboard-employee-shortcut dashboard-employee-shortcut-empty" onClick={() => navigate('/employees')}>
                  <span className="dashboard-employee-avatar"><RobotOutlined /></span>
                  <span className="dashboard-employee-shortcut-info">
                    <strong>暂无数字人</strong>
                    <small>前往创建数字员工</small>
                  </span>
                  <ArrowRightOutlined />
                </button>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card className="dashboard-status-card" variant="borderless" loading={loading}>
            <div className="dashboard-section-title">
              <div>
                <h3>应用快捷入口</h3>
                <p>点击访问应用。</p>
              </div>
              <Button type="link" onClick={() => navigate('/applications')}>全部应用</Button>
            </div>
            <div className="dashboard-app-shortcuts">
              {quickApplications.map((application) => (
                <button className="dashboard-app-shortcut" key={application.id} onClick={() => openStandaloneApplication(application)}>
                  <span className="dashboard-app-shortcut-icon">
                    {application.icon && (application.icon.startsWith('http') || application.icon.startsWith('data:') || application.icon.startsWith('/uploads/') || application.icon.startsWith('/avatars/') || application.icon.startsWith('/app-icons/')) ? (
                      <img src={resolveUrl(application.icon)} alt={application.name} />
                    ) : application.icon ? (
                      <span>{application.icon}</span>
                    ) : (
                      <span>{application.name?.charAt(0)?.toUpperCase() || 'A'}</span>
                    )}
                  </span>
                  <span className="dashboard-app-shortcut-info">
                    <strong>{application.name || '未命名应用'}</strong>
                    <small>{application.description || '暂无应用描述'}</small>
                  </span>
                  <span className="dashboard-shortcut-action dashboard-shortcut-action-visit"><ExportOutlined />访问</span>
                </button>
              ))}
              {!loading && quickApplications.length === 0 && (
                <button className="dashboard-app-shortcut dashboard-app-shortcut-empty" onClick={() => navigate('/applications')}>
                  <span className="dashboard-app-shortcut-icon"><AppstoreOutlined /></span>
                  <span className="dashboard-app-shortcut-info">
                    <strong>暂无应用</strong>
                    <small>前往创建应用</small>
                  </span>
                  <span className="dashboard-shortcut-action dashboard-shortcut-action-visit"><ExportOutlined />访问</span>
                </button>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
